import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useI18n } from "@/i18n/I18nContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import BusinessCard from "@/components/site/BusinessCard";
import { API } from "@/lib/api";
import { Loader2 } from "lucide-react";

const TYPES = [
  { value: "all", labelKey: "tab_all" },
  { value: "hotel", labelKey: "tab_hotel" },
  { value: "restaurant", labelKey: "tab_restaurant" },
  { value: "diving", labelKey: "tab_diving" },
  { value: "shop", labelKey: "tab_shop" },
  { value: "activity", labelKey: "tab_activity" },
];

export default function DirectoryPage() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");

  useEffect(() => {
    axios.get(`${API}/businesses`).then((r) => {
      setItems(r.data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const list = tab === "all" ? items : items.filter((b) => b.type === tab);
    return [...list].sort((a, b) => {
      const aw = (a.featured ? 2 : 0) + (a.sponsored ? 1 : 0);
      const bw = (b.featured ? 2 : 0) + (b.sponsored ? 1 : 0);
      return bw - aw;
    });
  }, [items, tab]);

  return (
    <main className="min-h-screen" data-testid="directory-page">
      <section className="mx-auto max-w-7xl px-6 lg:px-10 pt-28 pb-10">
        <p className="text-xs uppercase tracking-[0.3em] text-[var(--brand-primary)] font-bold mb-4">Directory</p>
        <h1 className="font-display text-stone-900 text-5xl sm:text-7xl tracking-tight leading-[0.95]">
          {t("directory_title")}
        </h1>
        <p className="mt-6 text-stone-600 max-w-2xl text-lg">{t("directory_subtitle")}</p>
      </section>

      <section className="mx-auto max-w-7xl px-6 lg:px-10 pb-24">
        <Tabs value={tab} onValueChange={setTab} data-testid="directory-tabs">
          <TabsList className="bg-[var(--bg-surface)] p-1.5 rounded-full h-auto flex-wrap">
            {TYPES.map((tp) => (
              <TabsTrigger
                key={tp.value}
                value={tp.value}
                className="rounded-full data-[state=active]:bg-[var(--brand-primary)] data-[state=active]:text-white px-5 py-2 text-sm"
                data-testid={`tab-${tp.value}`}
              >
                {t(tp.labelKey)}
              </TabsTrigger>
            ))}
          </TabsList>

          {TYPES.map((tp) => (
            <TabsContent key={tp.value} value={tp.value} className="mt-10">
              {loading ? (
                <div className="flex items-center gap-2 text-stone-500" data-testid="loading">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                </div>
              ) : filtered.length === 0 ? (
                <p className="text-stone-500" data-testid="empty">No listings yet.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {filtered.map((b) => <BusinessCard key={b.id} business={b} />)}
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </section>
    </main>
  );
}
